/**
 * WHAT: UAR-admin worksheet for the post-attestation DBA hand-off. Shows every
 *       attestation decision that requires DBA action (Removed / Modify) for a
 *       selected campaign, each row with paste-ready remediation CQL, plus
 *       fields to stamp in a ticket id and mark the row's action status.
 *
 * WHY:  Closes the loop between attester → UAR admin → DBA. The SQL is generated
 *       by the backend from reviewed templates
 *       (docs/remediation/cassandra_remediation_templates.md), so what the admin
 *       copies is what will be in the ticket. The action status field is what
 *       the post-attestation-validation report will key off of later.
 *
 * Scope (v1): Cassandra only. Other technologies show a friendly "not yet
 * implemented" message when selected — the backend already returns supported=false
 * for non-Cassandra campaigns.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import API_CONFIG from '../config/api.config';
import { useAuth } from '../context/AuthContext';
import { WideModal } from './common';

const ACTION_STATUS_OPTIONS = [
  { value: 'PENDING_DBA',    label: 'Pending DBA',    color: 'text-secondary' },
  { value: 'TICKETED',       label: 'Ticketed',       color: 'text-info' },
  { value: 'COMPLETED',      label: 'Completed',      color: 'text-success' },
  { value: 'NOT_APPLICABLE', label: 'Not Applicable', color: 'text-muted' },
];

const statusLabel = (value) => ACTION_STATUS_OPTIONS.find(o => o.value === value)?.label || value;
const statusColor = (value) => ACTION_STATUS_OPTIONS.find(o => o.value === value)?.color || 'text-muted';

const AttestationActions = () => {
  const { user } = useAuth();

  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [loading, setLoading] = useState(false);
  const [campaignMeta, setCampaignMeta] = useState(null);  // {campaign_name, database_type, campaign_status, supported, message}
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Filters
  const [filterDecision, setFilterDecision] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDb, setFilterDb] = useState('');
  const [searchText, setSearchText] = useState('');

  // Modal — shows the full SQL for one row
  const [sqlModalRow, setSqlModalRow] = useState(null);

  // Only COMPLETED campaigns are eligible. Mid-flight campaigns (ATTESTATION /
  // RUNNING) are excluded because attesters can still change decisions, and we
  // don't want UAR admins opening tickets based on provisional data.
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/'));
        const data = await resp.json();
        const list = Array.isArray(data) ? data : (data.campaigns || []);
        const actionable = list.filter(c =>
          (c.status || '').toUpperCase() === 'COMPLETED'
        );
        setCampaigns(actionable);
      } catch (e) {
        setError('Failed to load campaigns: ' + e.message);
      }
    })();
  }, []);

  const loadRemediationRows = useCallback(async (campaignId) => {
    if (!campaignId) {
      setRows([]);
      setCampaignMeta(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(API_CONFIG.getDatabasesEndpoint(`remediation-actions/?campaign_id=${campaignId}`));
      const data = await resp.json();
      if (!data.success) {
        setError(data.error || 'Failed to load remediation rows');
        setRows([]);
        setCampaignMeta(null);
        return;
      }
      setCampaignMeta({
        campaign_name: data.campaign_name,
        database_type: data.database_type,
        campaign_status: data.campaign_status,
        supported: data.supported,
        message: data.message,
      });
      setRows(data.rows || []);
    } catch (e) {
      setError('Error loading remediation rows: ' + e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRemediationRows(selectedCampaignId);
  }, [selectedCampaignId, loadRemediationRows]);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (filterDecision) out = out.filter(r => r.decision === filterDecision);
    if (filterStatus)   out = out.filter(r => r.action_status === filterStatus);
    if (filterDb)       out = out.filter(r => r.database_name === filterDb);
    if (searchText) {
      const t = searchText.toLowerCase();
      out = out.filter(r =>
        (r.db_username || '').toLowerCase().includes(t) ||
        (r.database_name || '').toLowerCase().includes(t) ||
        (r.ticket_id || '').toLowerCase().includes(t) ||
        (r.attester_email || '').toLowerCase().includes(t)
      );
    }
    return out;
  }, [rows, filterDecision, filterStatus, filterDb, searchText]);

  const dbOptions = useMemo(() => {
    const s = new Set(rows.map(r => r.database_name).filter(Boolean));
    return [...s].sort();
  }, [rows]);

  const saveRow = async (row, patch) => {
    setError(null);
    try {
      const resp = await fetch(API_CONFIG.getDatabasesEndpoint('remediation-actions/update/'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: parseInt(selectedCampaignId, 10),
          database_name: row.database_name,
          db_username: row.db_username,
          ticket_id: patch.ticket_id !== undefined ? patch.ticket_id : row.ticket_id,
          action_status: patch.action_status !== undefined ? patch.action_status : row.action_status,
          notes: patch.notes !== undefined ? patch.notes : row.notes,
          updated_by: user?.username || 'unknown',
        }),
      });
      const data = await resp.json();
      if (!data.success) {
        setError(data.error || 'Save failed');
        return false;
      }
      setRows(prev => prev.map(r =>
        r.database_name === row.database_name && r.db_username === row.db_username
          ? { ...r, ticket_id: data.row.ticket_id, action_status: data.row.action_status, notes: data.row.notes, updated_by: data.row.updated_by, updated_at: data.row.updated_at }
          : r
      ));
      setSuccessMessage('Saved.');
      setTimeout(() => setSuccessMessage(null), 2000);
      return true;
    } catch (e) {
      setError('Error saving: ' + e.message);
      return false;
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccessMessage(label ? `${label} copied to clipboard` : 'Copied to clipboard');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  const stats = useMemo(() => {
    const s = { total: rows.length, pending: 0, ticketed: 0, completed: 0, na: 0 };
    for (const r of rows) {
      if (r.action_status === 'PENDING_DBA')        s.pending++;
      else if (r.action_status === 'TICKETED')      s.ticketed++;
      else if (r.action_status === 'COMPLETED')     s.completed++;
      else if (r.action_status === 'NOT_APPLICABLE') s.na++;
    }
    return s;
  }, [rows]);

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <i className="fas fa-wrench text-primary me-2"></i>
          Attestation Actions
        </h4>
      </div>

      <p className="text-muted">
        Review attestation decisions that require DBA action (Removed / Modify), copy the
        generated remediation SQL into a ticket, and track action status until the DBA confirms
        completion.
      </p>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>{error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="fas fa-check-circle me-2"></i>{successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
        </div>
      )}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-5">
              <label className="form-label small text-muted mb-1">Campaign</label>
              <select
                className="form-select"
                value={selectedCampaignId}
                onChange={(e) => setSelectedCampaignId(e.target.value)}
              >
                <option value="">-- Select a campaign --</option>
                {campaigns.map(c => (
                  <option key={c.campaign_id} value={c.campaign_id}>
                    {c.campaign_name} ({c.database_type} / {c.status})
                  </option>
                ))}
              </select>
            </div>
            {campaignMeta && (
              <div className="col-md-7">
                <div className="text-muted small">
                  <strong>{campaignMeta.campaign_name}</strong>
                  {' · '}
                  {campaignMeta.database_type}
                  {' · '}
                  <span className={campaignMeta.campaign_status === 'COMPLETED' ? 'text-success' : 'text-info'}>
                    {campaignMeta.campaign_status}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {campaignMeta && !campaignMeta.supported && (
        <div className="alert alert-warning">
          <i className="fas fa-info-circle me-2"></i>{campaignMeta.message}
        </div>
      )}

      {campaignMeta && campaignMeta.supported && (
        <>
          <div className="row g-3 mb-3">
            <StatCard title="Total Actions" value={stats.total} icon="fa-list-check" />
            <StatCard title="Pending DBA" value={stats.pending} icon="fa-hourglass-half" valueClass="text-secondary" />
            <StatCard title="Ticketed" value={stats.ticketed} icon="fa-ticket" valueClass="text-info" />
            <StatCard title="Completed" value={stats.completed} icon="fa-check" valueClass="text-success" />
          </div>

          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label small text-muted mb-1">Decision</label>
                  <select className="form-select" value={filterDecision} onChange={e => setFilterDecision(e.target.value)}>
                    <option value="">All</option>
                    <option value="Removed">Removed</option>
                    <option value="Modify">Modify</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small text-muted mb-1">Action Status</label>
                  <select className="form-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                    <option value="">All</option>
                    {ACTION_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small text-muted mb-1">Database</label>
                  <select className="form-select" value={filterDb} onChange={e => setFilterDb(e.target.value)}>
                    <option value="">All</option>
                    {dbOptions.map(db => <option key={db} value={db}>{db}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small text-muted mb-1">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="User, DB, ticket, attester..."
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-header bg-secondary text-white">
              <h6 className="mb-0">
                <i className="fas fa-table me-2"></i>
                Remediation Queue ({filteredRows.length}{filteredRows.length !== rows.length ? ` of ${rows.length}` : ''})
              </h6>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status"></div>
                  <div className="text-muted small mt-2">Loading...</div>
                </div>
              ) : filteredRows.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                  <div>No actions to display.</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Database</th>
                        <th>User</th>
                        <th>Decision</th>
                        <th>Current → Target</th>
                        <th>Attester</th>
                        <th>Remediation SQL</th>
                        <th style={{ width: '180px' }}>Ticket ID</th>
                        <th style={{ width: '160px' }}>Status</th>
                        <th style={{ width: '100px' }}>Save</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((r, i) => (
                        <RemediationRow
                          key={`${r.database_name}::${r.db_username}`}
                          row={r}
                          onOpenSql={() => setSqlModalRow(r)}
                          onCopySql={() => copyToClipboard(r.remediation_sql, `SQL for ${r.db_username}`)}
                          onSave={(patch) => saveRow(r, patch)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <WideModal
        show={!!sqlModalRow}
        onClose={() => setSqlModalRow(null)}
        title={sqlModalRow ? `Remediation SQL — ${sqlModalRow.db_username} on ${sqlModalRow.database_name}` : ''}
        headerIcon="fa-code"
        maxWidth="1100px"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setSqlModalRow(null)}>Close</button>
            {sqlModalRow && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => copyToClipboard(sqlModalRow.remediation_sql, `SQL for ${sqlModalRow.db_username}`)}
              >
                <i className="fas fa-copy me-2"></i>Copy SQL
              </button>
            )}
          </>
        }
      >
        {sqlModalRow && (
          <div>
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="text-muted small">Decision</div>
                <div><strong>{sqlModalRow.decision}</strong>
                  {sqlModalRow.target_privilege_level && <> → <code>{sqlModalRow.target_privilege_level}</code></>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-muted small">Current privilege (at attestation)</div>
                <div><code>{sqlModalRow.current_privilege_level || '—'}</code></div>
              </div>
            </div>
            {sqlModalRow.attestation_comment && (
              <div className="alert alert-light">
                <div className="text-muted small mb-1">Attester comment</div>
                <div>{sqlModalRow.attestation_comment}</div>
              </div>
            )}
            <pre className="bg-dark text-light p-3 rounded" style={{ maxHeight: '500px', overflow: 'auto', fontSize: '0.85rem' }}>
              {sqlModalRow.remediation_sql}
            </pre>
          </div>
        )}
      </WideModal>
    </div>
  );
};


const StatCard = ({ title, value, icon, valueClass = '' }) => (
  <div className="col-md-3">
    <div className="card shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>{title}</h6>
            <h2 className={`mb-0 ${valueClass}`}>{value}</h2>
          </div>
          <div className="text-muted">
            <i className={`fas ${icon} fa-2x`}></i>
          </div>
        </div>
      </div>
    </div>
  </div>
);


const RemediationRow = ({ row, onOpenSql, onCopySql, onSave }) => {
  // Local draft state so the UAR admin can edit multiple fields before saving.
  // Sync back from props whenever the server row updates (e.g. after save or reload).
  const [ticket, setTicket] = useState(row.ticket_id || '');
  const [status, setStatus] = useState(row.action_status || 'PENDING_DBA');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setTicket(row.ticket_id || '');
    setStatus(row.action_status || 'PENDING_DBA');
    setDirty(false);
  }, [row.ticket_id, row.action_status]);

  const changeTicket = (v) => { setTicket(v); setDirty(true); };
  const changeStatus = (v) => { setStatus(v); setDirty(true); };

  const handleSave = async () => {
    const ok = await onSave({ ticket_id: ticket || null, action_status: status });
    if (ok) setDirty(false);
  };

  return (
    <tr>
      <td><code>{row.database_name}</code></td>
      <td><strong>{row.db_username}</strong></td>
      <td>
        {row.decision === 'Removed'
          ? <span className="text-danger"><i className="fas fa-user-slash me-1"></i>Removed</span>
          : <span className="text-warning"><i className="fas fa-user-pen me-1"></i>Modify</span>}
      </td>
      <td>
        <code>{row.current_privilege_level || '—'}</code>
        {row.target_privilege_level && (
          <> <i className="fas fa-arrow-right mx-1 text-muted"></i> <code>{row.target_privilege_level}</code></>
        )}
      </td>
      <td><small className="text-muted">{row.attester_email || '—'}</small></td>
      <td>
        <button className="btn btn-sm btn-outline-primary me-1" onClick={onOpenSql} title="View full SQL">
          <i className="fas fa-eye"></i>
        </button>
        <button className="btn btn-sm btn-outline-secondary" onClick={onCopySql} title="Copy SQL">
          <i className="fas fa-copy"></i>
        </button>
      </td>
      <td>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="e.g. SNOW-12345"
          value={ticket}
          onChange={(e) => changeTicket(e.target.value)}
        />
      </td>
      <td>
        <select
          className={`form-select form-select-sm ${statusColor(status)}`}
          value={status}
          onChange={(e) => changeStatus(e.target.value)}
        >
          {ACTION_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>
      <td>
        <button
          className={`btn btn-sm ${dirty ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={handleSave}
          disabled={!dirty}
          title={dirty ? 'Save changes' : 'No changes'}
        >
          <i className="fas fa-save"></i>
        </button>
      </td>
    </tr>
  );
};

export default AttestationActions;
